import React, { useState, useMemo } from 'react';
import { computeApexScore, getApexScoreColor, APEX_SCORE_AXES } from '../../lib/apexScore';
import { computeSeasonProgress } from '../../lib/seasonObjective';
import { SeasonObjectiveModal } from '../shell/SeasonObjectiveCard';
import { useRankBenchmarks } from '../../hooks/useRankBenchmarks';

/**
 * APEX v7.8.1 — ApexCirclesRail
 *
 * Rail vertical de 3 jauges circulaires, fixé (sticky) à droite du Dashboard.
 * Remplace les anciennes cartes/barres (Ranking, APEX Score, Objectif saison).
 *
 * Nouveautés v7.8.1 :
 *   - Cercles plus gros (110px) + glow permanent + fond contrasté
 *   - Icône de palier maison dans le cercle Ranking
 *   - Édition de l'objectif de saison via le panneau de détail
 */

// ── Couleurs par palier (cohérent avec l'aesthetic dark/cyber) ──
const TIER_COLORS = {
  IRON: '#6b6b6b',
  BRONZE: '#a97142',
  SILVER: '#9aa4ad',
  GOLD: '#e4b04a',
  PLATINUM: '#4ec9b0',
  EMERALD: '#2ecc71',
  DIAMOND: '#5fa8e8',
  MASTER: '#a855f7',
  GRANDMASTER: '#e8474b',
  CHALLENGER: '#f4d03f'
};

/**
 * Icône de palier maison : losange à facettes, stylisé, cohérent avec
 * le site, zéro dépendance externe. Ailes/point ajoutés pour les paliers
 * Apex (GM/Chall) pour les différencier visuellement.
 */
function TierEmblem({ tier, size = 30 }) {
  const color = TIER_COLORS[tier] || '#a1a1aa';
  const APEX = ['MASTER', 'GRANDMASTER', 'CHALLENGER'];
  const isApex = APEX.includes(tier);

  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      {isApex && <circle cx="16" cy="16" r="15" fill={color} opacity="0.12" />}
      <path
        d="M16 3 L27 16 L16 29 L5 16 Z"
        stroke={color}
        strokeWidth="2"
        fill={color}
        fillOpacity="0.15"
        strokeLinejoin="round"
      />
      <path
        d="M16 9 L22 16 L16 23 L10 16 Z"
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        strokeLinejoin="round"
      />
      {tier === 'CHALLENGER' && <circle cx="16" cy="16" r="2.5" fill={color} />}
      {(tier === 'GRANDMASTER' || tier === 'CHALLENGER') && (
        <>
          <path d="M3 13 L7 16 L3 19" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
          <path d="M29 13 L25 16 L29 19" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
}

/**
 * Cercle de progression SVG — v7.8.1 (plus gros, glow permanent, fond).
 */
function ProgressCircle({ percent, label, value, sub, color, glow, onClick, emblemTier }) {
  const size = 110;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent || 0));
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div
      className="flex flex-col items-center gap-2 cursor-pointer group select-none"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${label}: ${value}`}
    >
      <div
        className="relative rounded-full transition-transform duration-200 group-hover:scale-105"
        style={{
          width: size,
          height: size,
          background: 'radial-gradient(circle, rgba(24,24,27,0.9) 0%, rgba(9,9,11,0.6) 70%)'
        }}
      >
        <svg width={size} height={size} className="-rotate-90 absolute inset-0">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgb(39 39 42)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{
              transition: 'stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1)',
              filter: `drop-shadow(0 0 6px ${glow})`
            }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          {emblemTier && <TierEmblem tier={emblemTier} size={26} />}
          <span
            className={emblemTier ? 'text-sm font-black leading-none' : 'text-2xl font-black leading-none'}
            style={{ color }}
          >
            {value}
          </span>
          {sub && <span className="text-[9px] text-zinc-500 font-mono">{sub}</span>}
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold group-hover:text-white transition">
        {label}
      </span>
    </div>
  );
}

function DetailPanel({ which, data, onClose, onEditSeason }) {
  if (!which) return null;
  return (
    <div
      className="absolute right-full mr-4 top-0 w-72 bg-zinc-950 border border-zinc-800 shadow-2xl p-4 z-50 rounded-xl"
      style={{ animation: 'apexFadeIn 0.15s ease-out' }}
    >
      <style>{`@keyframes apexFadeIn { from { opacity: 0; transform: translateX(8px); } to { opacity: 1; transform: translateX(0); } }`}</style>

      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-zinc-600 hover:text-zinc-300 transition text-lg leading-none w-5 h-5 flex items-center justify-center"
        aria-label="Fermer"
      >
        ×
      </button>

      {which === 'ranking' && (
        <>
          <div className="text-[10px] uppercase tracking-[0.2em] text-violet-400 font-bold mb-3">
            Ranking
          </div>
          <div className="flex items-center gap-3">
            {data.rankTier && <TierEmblem tier={data.rankTier} size={42} />}
            <div>
              <div className="text-2xl font-black text-white leading-tight">{data.rankLabel}</div>
              <div className="text-[11px] text-zinc-400">{data.rankSub}</div>
            </div>
          </div>
        </>
      )}

      {which === 'apex' && (
        <>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2"
            style={{ color: data.apexColor }}>
            APEX Score · Climb Readiness
          </div>
          {data.apexScore === null ? (
            <div className="text-[11px] text-zinc-500">{data.apexMessage}</div>
          ) : (
            <>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-3xl font-black" style={{ color: data.apexColor }}>
                  {data.apexScore}
                </span>
                <span className="text-xs text-zinc-600">/ 100</span>
                <span className="text-[10px] text-zinc-500 ml-auto">{data.apexMessage}</span>
              </div>
              <div className="space-y-2.5">
                {Object.entries(data.apexAxes || {}).map(([key, axis]) => {
                  const def = APEX_SCORE_AXES[key];
                  const target = data.apexBenchmarkTargets?.[key];
                  const c = axis.score >= 65 ? '#34d399'
                    : axis.score >= 40 ? '#fbbf24' : '#fb7185';

                  const formatVal = (k, v) => {
                    if (v == null || isNaN(v)) return '—';
                    switch(k) {
                      case 'cs_per_min':      return v.toFixed(1);
                      case 'kda':             return v.toFixed(2);
                      case 'lane_wr_15':      return `${Math.round(v)}%`;
                      case 'damage_per_min':  return Math.round(v);
                      case 'vision':          return Math.round(v);
                      case 'deaths_per_game': return v.toFixed(1);
                      case 'win_rate':        return `${Math.round(v)}%`;
                      default: return Math.round(v);
                    }
                  };

                  return (
                    <div key={key}>
                      {/* Label | toi → cible | score × poids */}
                      <div className="flex items-center justify-between text-[10px] mb-0.5 gap-1">
                        <span className="text-zinc-300 font-semibold w-20 shrink-0">{def.label}</span>
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <span className="font-mono font-bold" style={{ color: c }}>
                            {formatVal(key, axis.value)}
                          </span>
                          {target != null && (
                            <>
                              <span className="text-zinc-700">→</span>
                              <span className="font-mono text-zinc-500">{formatVal(key, target)}</span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="font-mono font-bold" style={{ color: c }}>
                            {Math.round(axis.score)}
                          </span>
                          <span className="bg-zinc-800 border border-zinc-700 text-zinc-500 text-[8px] font-bold px-1 py-px rounded-sm">
                            ×{def.weight.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="h-1 bg-zinc-900 overflow-hidden rounded-full">
                        <div className="h-full transition-all duration-500 rounded-full"
                          style={{ width: `${axis.score}%`, background: c }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {which === 'season' && (
        <>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">
              Objectif de saison
            </div>
            <button
              onClick={onEditSeason}
              className="text-[9px] uppercase tracking-wider font-bold text-zinc-500 hover:text-amber-400 transition"
            >
              {data.season ? 'Modifier' : 'Définir'}
            </button>
          </div>
          {data.season ? (
            <>
              <div className="text-sm font-bold text-white mb-2">{data.seasonDesc}</div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-2xl font-black text-amber-400">{data.seasonPercent}%</span>
                {data.seasonAchieved && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-400">
                    Atteint ✓
                  </span>
                )}
              </div>
              <div className="h-1.5 bg-zinc-900 overflow-hidden rounded-full mb-1.5">
                <div className="h-full bg-amber-400 rounded-full transition-all duration-700"
                  style={{ width: `${data.seasonPercent}%` }} />
              </div>
              <div className="flex justify-between text-[10px] font-mono text-zinc-500">
                <span>{data.seasonCurrent}</span>
                <span>{data.seasonTarget}</span>
              </div>
              {data.seasonCutoff && (
                <div className="text-[11px] text-amber-300/90 mt-2 pt-2 border-t border-zinc-800/50">
                  Cutoff actuel : <span className="font-bold">{data.seasonCutoff.cutoff} LP</span>
                  {' · '}tu es à <span className="font-bold">{data.seasonCutoff.lp} LP</span>
                  {data.seasonCutoff.remaining > 0 && (
                    <> · il te manque ~<span className="font-bold text-amber-400">{data.seasonCutoff.remaining} LP</span></>
                  )}
                </div>
              )}
              {data.seasonProjection?.available && (
                <div className="text-[10px] text-zinc-500 mt-2 pt-2 border-t border-zinc-800/50">
                  Projection : {data.seasonProjection.lpPerDay >= 0 ? '+' : ''}
                  {data.seasonProjection.lpPerDay} LP/j → {data.seasonProjection.projectedLabel}
                </div>
              )}
            </>
          ) : (
            <div className="text-[11px] text-zinc-500">
              Aucun objectif de saison défini. Clique sur « Définir ».
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ApexCirclesRail({ games, userSettings, rankSnapshots, seasonObjective, onSaveSeasonObjective, apexTiers }) {
  const [hovered, setHovered] = useState(null);
  const [showSeasonModal, setShowSeasonModal] = useState(false);
  const allGames = Array.isArray(games) ? games : [];

  const ranking = useMemo(() => {
    const tier = userSettings?.current_tier;
    if (!tier) return null;
    const APEX = ['MASTER', 'GRANDMASTER', 'CHALLENGER'];
    const cap = (s) => s ? s.charAt(0) + s.slice(1).toLowerCase() : '';
    const lp = userSettings.current_lp || 0;
    const rank = userSettings.current_rank;
    const label = APEX.includes(tier) ? cap(tier) : `${cap(tier)} ${rank || ''}`.trim();
    let pct = 50;
    if (APEX.includes(tier)) {
      pct = Math.min(100, (lp / 1000) * 100);
    } else {
      pct = Math.min(100, lp);
    }
    return { label, tier, sub: `${lp} LP`, pct };
  }, [userSettings]);

  const { benchmarks } = useRankBenchmarks(userSettings?.primary_role || 'ADC');

  const apex = useMemo(
    () => computeApexScore(allGames, userSettings, benchmarks),
    [allGames, userSettings, benchmarks]
  );
  const apexColors = getApexScoreColor(apex.status);

  const season = useMemo(() => {
    if (!seasonObjective || !userSettings) return null;
    return computeSeasonProgress(seasonObjective, userSettings, rankSnapshots || [], apexTiers);
  }, [seasonObjective, userSettings, rankSnapshots, apexTiers]);

  if (!userSettings?.current_tier) return null;

  const detailData = {
    rankLabel: ranking?.label || '—',
    rankTier: ranking?.tier,
    rankSub: ranking?.sub || '',
    apexScore: apex.score,
    apexMessage: apex.message,
    apexColor: apexColors.hex || '#22d3ee',
    apexAxes: apex.axes,
    apexBenchmarkTargets: apex.benchmarkTargets || {},
    season: !!season,
    seasonDesc: seasonObjective?.description || season?.targetLabel || '',
    seasonPercent: season?.percent ?? 0,
    seasonAchieved: season?.achieved || false,
    seasonCurrent: season?.currentLabel || '',
    seasonTarget: season?.targetLabel || '',
    seasonProjection: season?.projection,
    seasonCutoff: season?.cutoffInfo
  };

  return (
    <>
      <div
        className="hidden xl:flex fixed right-5 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-6 bg-zinc-950/85 backdrop-blur-md border border-zinc-800/70 rounded-3xl px-4 py-7 shadow-2xl"
      >
        <ProgressCircle
          percent={ranking?.pct || 0}
          label="Ranking"
          value={ranking?.sub?.replace(' LP', '') || '—'}
          sub="LP"
          color={TIER_COLORS[ranking?.tier] || '#a78bfa'}
          glow={`${TIER_COLORS[ranking?.tier] || '#a78bfa'}88`}
          emblemTier={ranking?.tier}
          onClick={() => setHovered(hovered === 'ranking' ? null : 'ranking')}
        />
        <div className="w-10 h-px bg-zinc-800" />
        <ProgressCircle
          percent={apex.score ?? 0}
          label="APEX"
          value={apex.score ?? '—'}
          sub="/ 100"
          color={apexColors.hex || '#22d3ee'}
          glow={apexColors.glow || 'rgba(34,211,238,0.5)'}
          onClick={() => setHovered(hovered === 'apex' ? null : 'apex')}
        />
        <div className="w-10 h-px bg-zinc-800" />
        <ProgressCircle
          percent={season?.percent ?? 0}
          label="Saison"
          value={season ? `${season.percent}%` : '—'}
          sub={season?.achieved ? '✓ atteint' : null}
          color="#fbbf24"
          glow="rgba(251,191,36,0.55)"
          onClick={() => setHovered(hovered === 'season' ? null : 'season')}
        />

        {hovered && (
          <DetailPanel
            which={hovered}
            data={detailData}
            onClose={() => setHovered(null)}
            onEditSeason={() => { setShowSeasonModal(true); setHovered(null); }}
          />
        )}
      </div>

      {showSeasonModal && (
        <SeasonObjectiveModal
          current={seasonObjective}
          onClose={() => setShowSeasonModal(false)}
          onSave={(obj) => {
            onSaveSeasonObjective?.(obj);
            setShowSeasonModal(false);
          }}
        />
      )}
    </>
  );
}
